import React, { useState } from 'react';
import { AlertTriangle, FileText, Info, Square } from 'lucide-react';

interface MinhaMesaInstructionsProps {
  instruction: string | null;
}

const IMAGE_URL_REGEX = /^(https?:\/\/\S+\.(?:png|jpe?g|gif|webp|svg))$/i;
const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\((https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp|svg))\)/i;

// Regex: bold first word if it's a common Portuguese action verb
const ACTION_VERB_REGEX = /^(Cadastrar|Vincular|Definir|Preencher|Verificar|Conferir|Selecionar|Inserir|Registrar|Confirmar|Checar|Abrir|Fechar|Salvar|Enviar|Criar|Atualizar|Lançar|Validar|Informar|Assinar|Anexar|Revisar|Acionar|Marcar|Descrever|Identificar|Calcular|Aprovar|Liberar|Realizar|Acessar|Digitalizar|Imprimir|Digitalizar|Comunicar|Notificar|Classificar|Aprovar|Solicitar|Executar|Pagar|Cobrar|Encaminhar|Retornar)\b/;

function renderInlineWithVerbBold(text: string, keyPrefix: string) {
  const verbMatch = text.match(ACTION_VERB_REGEX);
  if (verbMatch) {
    const verb = verbMatch[0];
    const rest = text.slice(verb.length);
    return (
      <>
        <strong key={`${keyPrefix}-verb`} className="font-bold text-slate-900 dark:text-white">{verb}</strong>
        {renderInlineHighlights(rest, keyPrefix)}
      </>
    );
  }
  return renderInlineHighlights(text, keyPrefix);
}

function renderInlineHighlights(text: string, keyPrefix: string) {
  return text.split(/(\*\*.*?\*\*)/g).map((chunk, index) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold text-slate-900 dark:text-white">
          {chunk.slice(2, -2)}
        </strong>
      );
    }

    return <React.Fragment key={`${keyPrefix}-${index}`}>{chunk}</React.Fragment>;
  });
}

export const MinhaMesaInstructions: React.FC<MinhaMesaInstructionsProps> = ({
  instruction,
}) => {
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setChecked(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  if (!instruction) {
    return (
      <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
        Nenhuma instrução SOP definida para esta etapa.
      </p>
    );
  }

  const blocks = instruction
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
        <FileText size={13} />
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.14em]">SOP · Como executar</span>
      </div>

      <div className="space-y-3 text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
        {blocks.map((block, index) => {
          const markdownImage = block.match(MARKDOWN_IMAGE_REGEX)?.[1] ?? null;
          const imageUrl = markdownImage ?? (IMAGE_URL_REGEX.test(block) ? block : null);

          if (imageUrl) {
            return (
              <img
                key={`${imageUrl}-${index}`}
                src={imageUrl}
                alt={`Instrução ${index + 1}`}
                className="max-h-[320px] w-full rounded-[4px] border border-slate-200 object-contain dark:border-slate-700"
              />
            );
          }

          const calloutMatch = block.match(/^(aten[cç][aã]o|alerta|obs|nota)\s*:\s*(.*)$/i);
          if (calloutMatch) {
            const kind = calloutMatch[1].toLowerCase();
            const content = calloutMatch[2];
            const isAlert = kind.includes('aten') || kind.includes('alerta');

            return (
              <div
                key={`${block}-${index}`}
                className={`rounded-[4px] border px-3 py-2 ${isAlert ? 'border-amber-500/40 bg-amber-500/10 text-amber-100' : 'border-blue-500/30 bg-blue-500/10 text-slate-100'}`}
              >
                <div className="flex items-start gap-2">
                  {isAlert ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-300" /> : <Info size={14} className="mt-0.5 shrink-0 text-blue-300" />}
                  <p className="whitespace-pre-wrap text-[12px] leading-relaxed">
                    {renderInlineHighlights(content, `callout-${index}`)}
                  </p>
                </div>
              </div>
            );
          }

          const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
          const isOrderedList = lines.every((line) => /^\d+\.\s+/.test(line));
          const isBulletList = lines.every((line) => /^[-*•]\s+/.test(line));

          if (isOrderedList) {
            const totalItems = lines.length;
            const checkedCount = lines.filter((_, li) => checked.has(`${index}-${li}`)).length;
            return (
              <div key={`${block}-${index}`}>
                {checkedCount > 0 && (
                  <p className="mb-1.5 font-mono text-[8px] text-emerald-600 dark:text-emerald-400">
                    {checkedCount}/{totalItems} concluídos
                  </p>
                )}
                <ol className="space-y-1.5">
                  {lines.map((line, lineIndex) => {
                    const number = line.match(/^(\d+)\./)?.[1] ?? `${lineIndex + 1}`;
                    const content = line.replace(/^\d+\.\s+/, '');
                    const itemKey = `${index}-${lineIndex}`;
                    const isItemChecked = checked.has(itemKey);
                    return (
                      <li
                        key={`${line}-${lineIndex}`}
                        className={`flex cursor-pointer items-start gap-2 rounded-[4px] border px-3 py-2 transition-colors select-none ${
                          isItemChecked
                            ? 'border-emerald-500/30 bg-emerald-500/10 dark:border-emerald-700/40 dark:bg-emerald-900/20'
                            : 'border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:bg-slate-800'
                        }`}
                        onClick={() => toggle(itemKey)}
                      >
                        <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold transition-colors ${
                          isItemChecked
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200'
                        }`}>
                          {isItemChecked ? '✓' : number}
                        </span>
                        <span className={`transition-opacity ${isItemChecked ? 'line-through opacity-40' : ''}`}>
                          {renderInlineHighlights(content, `ordered-${index}-${lineIndex}`)}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          }

          if (isBulletList) {
            const totalItems = lines.length;
            const checkedCount = lines.filter((_, li) => checked.has(`b${index}-${li}`)).length;
            return (
              <div key={`${block}-${index}`}>
                {checkedCount > 0 && (
                  <p className="mb-1.5 font-mono text-[8px] text-emerald-600 dark:text-emerald-400">
                    {checkedCount}/{totalItems} concluídos
                  </p>
                )}
                <ul className="space-y-1.5">
                  {lines.map((line, lineIndex) => {
                    const content = line.replace(/^[-*•]\s+/, '');
                    const itemKey = `b${index}-${lineIndex}`;
                    const isItemChecked = checked.has(itemKey);
                    return (
                      <li
                        key={`${line}-${lineIndex}`}
                        className={`flex cursor-pointer items-start gap-2 rounded-[4px] px-1 py-1 transition-colors select-none ${
                          isItemChecked ? 'opacity-40' : 'hover:bg-slate-100 dark:hover:bg-slate-800/40'
                        }`}
                        onClick={() => toggle(itemKey)}
                      >
                        <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[9px] font-bold transition-colors ${
                          isItemChecked
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-slate-400 dark:border-slate-500'
                        }`}>
                          {isItemChecked && '✓'}
                        </span>
                        <span className={`transition-all ${isItemChecked ? 'line-through' : ''}`}>
                          {renderInlineHighlights(content, `bullet-${index}-${lineIndex}`)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }

          // Plain paragraph — split into lines, each gets a Square icon + verb bolding
          const paraLines = block.split('\n').map(l => l.trim()).filter(Boolean);
          return (
            <div key={`${block}-${index}`} className="space-y-1.5">
              {paraLines.map((line, li) => (
                <div key={`${block}-${index}-${li}`} className="flex items-start gap-2">
                  <Square size={9} className="mt-[3px] shrink-0 text-slate-300 dark:text-slate-600" />
                  <span className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
                    {renderInlineWithVerbBold(line, `para-${index}-${li}`)}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
